import { computed, ref } from 'vue'
import { freezeObject, hasProperty, isArray, isFunction, isObject } from '@/global/utils/utils'
import { dateService, FORMATS } from '@/global/services/date'

const TAB_KEY = 'basic_info'

// The API entity has its own top-level `data` JSON column (see
// IncomingRequestDTO::toArray()). Content.vue's generic refreshData() always
// tries to unwrap ONE level of `.data` from whatever it's given, assuming
// that's an envelope like `{ data: <entity> }` - it can't tell the entity's
// OWN `data` field apart from that envelope. Whenever this request already
// has a non-empty `data` column (eg. it has locations), refreshData()
// mistakes that column for the envelope and reduces formDataForSend /
// originalFormData down to just its contents, wiping out every other field
// (name, description, ...) - which is why a cancel (which reverts the form
// to a clone of originalFormData) appeared to clear the form. `onDataReceived`
// below runs before refreshData() and renames the column out of its way.
const RAW_DATA_KEY = '__jsonData'

function extractLocations (data) {
  if (isArray(data?.[RAW_DATA_KEY]?.locations)) {
    return data[RAW_DATA_KEY].locations
  }
  if (isArray(data?.data?.locations)) {
    return data.data.locations
  }
  return []
}

export function useBasicInfo (options) {
  const {
    translate,
    route,
    tabActionsComponent,
    columnsContentComponent
  } = options || {}

  const storedData = ref({
    source_id: null,
    foreign_id: null,
    locations: []
  })

  const tagOptions = ref([])

  // Backend-driven per-tab permission - locked by default; only an explicit
  // `meta.can_edit === true` from the API unlocks the tab.
  const canEdit = ref(false)

  const apiConfig = freezeObject({
    post: {
      module: 'road-maintenance',
      route: `incoming-requests/traffic-regulation/${route.params.id}`
    },
    get: {
      module: 'road-maintenance',
      route: `incoming-requests/traffic-regulation/${route.params.id}`
    }
  })

  const resolveSourceDisplay = (data) => {
    const sourceDescription = data?.source?.description || null
    const sourceName = data?.source?.name || null

    if (sourceDescription) {
      return translate(sourceDescription)
    }

    return sourceName
  }

  // Runs before Content.vue's refreshData() gets the fetched entity - move
  // its `data` JSON column out of the way so refreshData() can't mistake it
  // for a `{ data: <entity> }` envelope and lose the rest of the entity.
  const onDataReceived = (apiData) => {
    const entity = apiData?.data
    if (entity && isObject(entity) && hasProperty(entity, 'data')) {
      entity[RAW_DATA_KEY] = entity.data
      delete entity.data
    }
  }

  // Also used as `afterCancel`/`afterSave` - afterSave additionally passes
  // the just-saved entity as `freshData`, which (unlike `event.formDataForSend`,
  // Content.vue's own post-save state) is guaranteed to still have every field.
  const onDataFetched = (event, freshData) => {
    const { data: apiData, updateData } = event || {}
    const { formDataForSend } = event || {}
    const data = freshData || apiData?.data || formDataForSend || {}

    // Only the initial fetch (`apiData`) carries `meta`. afterSave re-runs this
    // with `freshData` and afterCancel with just the Content instance - neither
    // carries `meta`, so guard on its presence rather than resetting the
    // permission (which would hide the Edit button after a cancel).
    if (hasProperty(apiData?.meta || {}, 'can_edit')) {
      canEdit.value = apiData.meta.can_edit === true
    }

    storedData.value = {
      source_id: data?.source_id || null,
      foreign_id: data?.foreign_id || null,
      locations: extractLocations(data)
    }

    tagOptions.value = isArray(data?.tags) ? data.tags : []

    if (isFunction(updateData)) {
      const locations = extractLocations(data)
      const primaryLocation = locations[0] || {}

      updateData({
        // `name`/`description` are otherwise read straight off formDataForSend
        // by the fields renderer - re-assert them here too, since Content.vue's
        // own refreshData() (see the `onDataReceived` comment above) can still
        // reduce formDataForSend down to just the `data` column right after a
        // save (there's no equivalent hook to fix that up on that path).
        name: data?.name || null,
        description: data?.description || null,
        source_display: resolveSourceDisplay(data),
        // Kept separate from the raw `planned_start`/`planned_end` API keys
        // (which stay on the object in ISO format) so a form cancel - which
        // resets the form back to the raw fetched entity - can't clobber the
        // display-formatted value with the raw one before this callback gets
        // a chance to re-run and reformat it.
        planned_start_display: data?.planned_start
          ? dateService.formatCustom(data.planned_start, FORMATS.isoDatetime, FORMATS.datetimeShort)
          : null,
        planned_end_display: data?.planned_end
          ? dateService.formatCustom(data.planned_end, FORMATS.isoDatetime, FORMATS.datetimeShort)
          : null,
        address: primaryLocation?.address || null,
        city_district_id: data?.city_district_id || null,
        tag_ids: isArray(data?.tag_ids) ? data.tag_ids : []
      })
    }
  }

  const modifyDataBeforeSend = (event) => {
    const { copiedData } = event || {}

    copiedData.planned_start = copiedData?.planned_start_display
      ? dateService.formatCustom(copiedData.planned_start_display, FORMATS.datetimeShort, FORMATS.isoDatetime)
      : null

    copiedData.planned_end = copiedData?.planned_end_display
      ? dateService.formatCustom(copiedData.planned_end_display, FORMATS.datetimeShort, FORMATS.isoDatetime)
      : null

    const existingLocations = storedData.value.locations
    if (existingLocations.length) {
      copiedData.locations = existingLocations.map((location, index) => ({
        id: location?.id || null,
        geolocation_id: location?.geolocation_id || null,
        address: index === 0 ? copiedData?.address : location?.address,
        city_district_id: index === 0 ? copiedData?.city_district_id : location?.city_district_id
      }))
    }
    else {
      copiedData.locations = [
        {
          geolocation_id: null,
          address: copiedData?.address || null,
          city_district_id: copiedData?.city_district_id || null
        }
      ]
    }

    copiedData.foreign_id = storedData.value.foreign_id
    copiedData.source_id = storedData.value.source_id

    delete copiedData.address
    delete copiedData.city_district_id
    delete copiedData.source_display
    delete copiedData.planned_start_display
    delete copiedData.planned_end_display

    return copiedData
  }

  const tabActions = computed(() => ({
    component: tabActionsComponent,
    title: translate('fleet/vehicle.basic_info'),
    showEditButtons: true,
    showEditBtn: canEdit.value,
    showBackButton: {
      uiViewName: 'TemporaryRegulationsRequests'
    },
    visible: true
  }))

  const fieldsBlock = computed(() => ({
    type: 'fields',
    columnsCount: 2,
    fields: [
      {
        key: 'name',
        label: translate('road-maintenance/temporary_regulation/requests.name'),
        creatable: true,
        editable: true,
        visible: true
      },
      {
        key: 'source_display',
        label: translate('road-maintenance/temporary_regulation/requests.source'),
        creatable: true,
        editable: false,
        visible: true
      },
      {
        key: 'description',
        label: translate('road-maintenance/patrol-service.description'),
        type: 'textarea',
        creatable: true,
        editable: true,
        visible: true,
        colSpan: 2
      },
      {
        key: 'planned_start_display',
        label: translate('road-maintenance/work_orders.estimated_start_date'),
        type: 'datetime-picker',
        appendIcon: 'mdi-calendar-month',
        format: FORMATS.datetimeShort,
        placeholder: FORMATS.datetimeShort,
        creatable: true,
        editable: true,
        visible: true
      },
      {
        key: 'planned_end_display',
        label: translate('road-maintenance/work_orders.estimated_end_date'),
        type: 'datetime-picker',
        appendIcon: 'mdi-calendar-month',
        format: FORMATS.datetimeShort,
        placeholder: FORMATS.datetimeShort,
        creatable: true,
        editable: true,
        visible: true
      },
      {
        key: 'address',
        label: translate('road-maintenance/patrol-service.address'),
        creatable: true,
        editable: true,
        visible: true
      },
      {
        key: 'city_district_id',
        label: translate('road-maintenance/temporary_regulation.city_district'),
        type: 'autocomplete',
        creatable: true,
        editable: true,
        visible: true,
        autocomplete_options: {
          module: 'road-maintenance',
          route: 'traffic-regulation/city-districts',
          allowSearch: false
        }
      },
      {
        key: 'tag_ids',
        label: translate('road-maintenance.tags'),
        type: 'autocomplete',
        creatable: true,
        editable: true,
        visible: tagOptions.value.length > 0,
        colSpan: 2,
        items: tagOptions.value,
        props: {
          multiple: true,
          chips: true,
          smallChips: true,
          deletableChips: true
        },
        autocomplete_options: {
          custom_display_value: (item) => {
            if (!item) return null
            return item?.description ? translate(item.description) : (item?.name || null)
          }
        }
      }
    ]
  }))

  const basicInfoBlock = computed(() => ({
    component: columnsContentComponent,
    visible: true,
    showButtons: false,
    type: 'columns',
    rows: [
      fieldsBlock.value
    ]
  }))

  return computed(() => ({
    title: translate('fleet/vehicle.basic_info'),
    key: TAB_KEY,
    apiConfig,
    // Consumed by Content.vue -> FieldsRenderer.vue's `unlockFieldsOnDblClick`
    // to block the double-click-to-edit shortcut too - hiding the Edit button
    // alone doesn't stop that path from unlocking the fields.
    permissions: { canEdit: canEdit.value },
    modifyDataBeforeSend,
    onDataReceived,
    dataFetched: onDataFetched,
    afterCancel: onDataFetched,
    afterSave: onDataFetched,
    tabContent: [
      tabActions.value,
      basicInfoBlock.value
    ]
  }))
}
